import React, { useState } from 'react';
import { useEditor } from '../contexts/EditorContext';
import { useLanguage } from '../i18n';
import { Trash2, Type, Image, EyeOff, Eye, GripVertical, Pencil, Shapes } from 'lucide-react';

interface Props {
  selectedId: string | null;
  setSelectedId: (id: string) => void;
}

const ElementsList: React.FC<Props> = ({ selectedId, setSelectedId }) => {
  const { elements, removeElement, moveElement, updateElement, setElements } = useEditor();
  const { t } = useLanguage();
  const [draggedId, setDraggedId] = useState<string | null>(null);
  const [dragOverId, setDragOverId] = useState<string | null>(null);
  const [editingNameId, setEditingNameId] = useState<string | null>(null);
  
  // عكس ترتيب العناصر (الأول يصبح الأخير والعكس) - مثل Photoshop
  const reversedElements = [...elements].reverse();
  
  // Handle drag start
  const handleDragStart = (e: React.DragEvent, el: any, originalIdx: number) => {
    setDraggedId(el.id);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/html', el.id);
  };
  
  // Handle drag over
  const handleDragOver = (e: React.DragEvent, targetId: string) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (targetId !== draggedId) {
      setDragOverId(targetId);
    }
  };
  
  // Handle drop
  const handleDrop = (e: React.DragEvent, targetEl: any, targetReversedIdx: number) => {
    e.preventDefault();
    setDragOverId(null);
    
    if (!draggedId || draggedId === targetEl.id) {
      setDraggedId(null);
      return;
    }
    
    // حساب الفهرس الأصلي للعنصر المستهدف
    const targetOriginalIdx = elements.length - 1 - targetReversedIdx;
    const draggedOriginalIdx = elements.findIndex(el => el.id === draggedId);
    
    if (draggedOriginalIdx === -1 || targetOriginalIdx === -1) {
      setDraggedId(null);
      return;
    }
    
    // إعادة ترتيب العناصر
    const newElements = [...elements];
    const [draggedElement] = newElements.splice(draggedOriginalIdx, 1);
    newElements.splice(targetOriginalIdx, 0, draggedElement);
    
    // تحديث order
    const updatedElements = newElements.map((el, i) => ({ ...el, order: i }));
    setElements(updatedElements);
    setDraggedId(null);
  };
  
  // Handle drag end
  const handleDragEnd = () => {
    setDraggedId(null);
    setDragOverId(null);
  };
  
  return (
    <div>
      {elements.length === 0 && (
        <div style={{ 
          color: '#525252', 
          textAlign: 'center', 
          padding: '20px 0', 
          fontSize: 10,
          fontFamily: 'system-ui, -apple-system, sans-serif',
          textTransform: 'uppercase',
          letterSpacing: '0.5px'
        }}>
          {t('components.elementsList.noElements')}
        </div>
      )}
      {reversedElements.map((el, reversedIdx) => {
        // حساب الفهرس الأصلي (للاستخدام في moveElement)
        const originalIdx = elements.length - 1 - reversedIdx;
        const isDragging = draggedId === el.id;
        const isDragOver = dragOverId === el.id;
        
        return (
          <div
            key={el.id}
            draggable
            onDragStart={(e) => handleDragStart(e, el, originalIdx)}
            onDragOver={(e) => handleDragOver(e, el.id)}
            onDrop={(e) => handleDrop(e, el, reversedIdx)}
            onDragEnd={handleDragEnd}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              background: selectedId === el.id ? '#2a2a2a' : isDragOver ? '#333' : isDragging ? '#1a1a1a' : 'transparent',
              borderRadius: 2,
              padding: '6px 4px',
              marginBottom: 2,
              cursor: 'grab',
              border: selectedId === el.id ? '1px solid rgba(255, 255, 255, 0.2)' : isDragOver ? '1px solid rgba(59, 130, 246, 0.5)' : '1px solid transparent',
              transition: 'all 0.1s',
              fontFamily: 'system-ui, -apple-system, sans-serif',
              opacity: isDragging ? 0.5 : 1
            }}
            onClick={() => setSelectedId(el.id)}
            onMouseEnter={e => {
              if (selectedId !== el.id && !isDragging) {
                e.currentTarget.style.background = '#252525';
                e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.1)';
              }
            }}
            onMouseLeave={e => {
              if (selectedId !== el.id && !isDragOver) {
                e.currentTarget.style.background = 'transparent';
                e.currentTarget.style.borderColor = 'transparent';
              }
            }}
          >
            {/* Drag Handle */}
            <div
              style={{
                cursor: 'grab',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '2px',
                opacity: 0.5,
                transition: 'opacity 0.15s'
              }}
              onMouseEnter={e => e.currentTarget.style.opacity = '1'}
              onMouseLeave={e => e.currentTarget.style.opacity = '0.5'}
            >
              <GripVertical size={14} color="#71717a" />
            </div>
            
            {/* Type Icon */}
            <div style={{ 
              width: 24, 
              height: 24, 
              background: el.type === 'text' ? 'rgba(59, 130, 246, 0.15)' : el.type === 'image' ? 'rgba(34, 197, 94, 0.15)' : 'rgba(168, 85, 247, 0.15)',
              borderRadius: 2,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              border: `1px solid ${el.type === 'text' ? 'rgba(59, 130, 246, 0.3)' : el.type === 'image' ? 'rgba(34, 197, 94, 0.3)' : 'rgba(168, 85, 247, 0.3)'}`,
              flexShrink: 0
            }}>
              {el.type === 'text' ? <Type size={14} color="#60a5fa" /> : el.type === 'image' ? <Image size={14} color="#4ade80" /> : <Shapes size={14} color="#a855f7" />}
            </div>
            
            {/* Name Badge */}
            <span style={{ 
              color: '#71717a', 
              fontSize: 9,
              background: 'rgba(59, 130, 246, 0.1)',
              borderRadius: 2,
              padding: '2px 5px',
              direction: 'ltr',
              fontFamily: 'monospace',
              minWidth: 35,
              textAlign: 'center',
              display: 'inline-block',
              border: '1px solid rgba(59, 130, 246, 0.2)',
              flexShrink: 0
            }}>
              {el.name || 'id'}
            </span>
            
            {/* Name Input */}
            {editingNameId === el.id && (
              <input
                autoFocus
                value={el.name || ''}
                onChange={e => updateElement(el.id, { name: e.target.value.replace(/[^a-zA-Z0-9\-_]/g, '') })}
                onBlur={() => setEditingNameId(null)}
                onKeyDown={e => {
                  if (e.key === 'Enter') {
                    setEditingNameId(null);
                  } else if (e.key === 'Escape') {
                    setEditingNameId(null);
                  }
                }}
                placeholder="name"
                style={{ 
                  width: 70, 
                  fontSize: 10, 
                  background: '#0a0a0a',
                  color: '#d4d4d8',
                  border: '1px solid rgba(59, 130, 246, 0.5)', 
                  borderRadius: 2, 
                  padding: '3px 5px', 
                  direction: 'ltr',
                  fontFamily: 'monospace',
                  flexShrink: 0,
                  outline: 'none'
                }}
                onClick={e => e.stopPropagation()}
              />
            )}
            
            {/* Content Preview */}
            <span style={{ 
              flex: 1, 
              color: '#a1a1aa', 
              fontSize: 10, 
              overflow: 'hidden', 
              textOverflow: 'ellipsis', 
              whiteSpace: 'nowrap',
              fontFamily: 'system-ui, -apple-system, sans-serif',
              minWidth: 0
            }}>
              {el.type === 'text' ? (el.text?.slice(0, 15) || '...') : el.type === 'image' ? <Image size={12} color="#71717a" style={{ display: 'inline-block', verticalAlign: 'middle' }} /> : <Shapes size={12} color="#71717a" style={{ display: 'inline-block', verticalAlign: 'middle' }} />}
            </span>
            
            {/* Toggle Visibility Button */}
            <button 
              onClick={e => { 
                e.stopPropagation(); 
                updateElement(el.id, { hideInRunner: !el.hideInRunner });
              }} 
              title={el.hideInRunner ? t('components.elementsList.showInRunner') : t('components.elementsList.hideFromRunner')}
              style={{ 
                border: 'none', 
                background: 'transparent', 
                borderRadius: 2,
                width: 22,
                height: 22,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                opacity: 0.7,
                transition: 'all 0.15s',
                flexShrink: 0
              }}
              onMouseEnter={e => {
                e.currentTarget.style.opacity = '1';
                e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)';
              }}
              onMouseLeave={e => {
                e.currentTarget.style.opacity = '0.7';
                e.currentTarget.style.background = 'transparent';
              }}
            >
              {el.hideInRunner ? <EyeOff size={14} color="#71717a" /> : <Eye size={14} color="#71717a" />}
            </button>
            
            {/* Edit Name Button */}
            <button 
              onClick={e => { 
                e.stopPropagation(); 
                setEditingNameId(el.id);
              }} 
              title="Edit name"
              style={{ 
                border: 'none', 
                background: 'transparent', 
                borderRadius: 2,
                width: 22,
                height: 22,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                opacity: 0.7,
                transition: 'all 0.15s',
                flexShrink: 0
              }}
              onMouseEnter={e => { 
                e.currentTarget.style.opacity = '1'; 
                e.currentTarget.style.background = 'rgba(59, 130, 246, 0.15)';
              }}
              onMouseLeave={e => {
                e.currentTarget.style.opacity = '0.7';
                e.currentTarget.style.background = 'transparent';
              }}
            >
              <Pencil size={14} color="#3b82f6" />
            </button>
            
            {/* Delete Button */}
            <button 
              onClick={e => { e.stopPropagation(); removeElement(el.id); }} 
              title={t('common.delete')} 
              style={{ 
                border: 'none', 
                background: 'transparent', 
                borderRadius: 2,
                width: 22,
                height: 22,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                opacity: 0.7,
                transition: 'all 0.15s',
                flexShrink: 0
              }}
              onMouseEnter={e => { 
                e.currentTarget.style.opacity = '1'; 
                e.currentTarget.style.background = 'rgba(239, 68, 68, 0.15)';
              }}
              onMouseLeave={e => {
                e.currentTarget.style.opacity = '0.7';
                e.currentTarget.style.background = 'transparent';
              }}
            >
              <Trash2 size={14} color="#ef4444" />
            </button>
          </div>
        );
      })}
    </div>
  );
};

export default ElementsList;
